import { FC, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  checklistReservaService,
  ehForaDoEscopo,
  mensagemErroChecklistReserva,
} from '@/services/checklistReservaService'
import { ROTULOS_CODIGO_MOTIVO, ROTULOS_DECISAO_ITEM, ROTULOS_STATUS_ITEM } from '@/constants/statusChecklist'
import type { DecisaoItemChecklistResponse } from '@/types/checklist'
import type { ItemChecklistReserva } from '@/types/estruturacao'
import { formatDate, formatDateTime } from '@/utils/formatters'
import { rotuloDoItem } from '@/utils/checklistReserva'

interface Props {
  open: boolean
  reservaId: string
  item: ItemChecklistReserva | null
  /** Back-office vê o autor; o consultor nunca (o backend já omite — a tela não exibe de todo modo). */
  mostrarAutor: boolean
  onFechar: () => void
}

type Estado = 'carregando' | 'ok' | 'erro' | 'semPermissao'

/** Histórico de decisões do item (append-only), mais recente primeiro. */
export const HistoricoDecisoesDialog: FC<Props> = ({ open, reservaId, item, mostrarAutor, onFechar }) => {
  const [decisoes, setDecisoes] = useState<DecisaoItemChecklistResponse[]>([])
  const [estado, setEstado] = useState<Estado>('carregando')
  const [erro, setErro] = useState<string | null>(null)
  const [tentativa, setTentativa] = useState(0)

  useEffect(() => {
    if (!open || !item?.chave) return
    let vivo = true
    setEstado('carregando')
    checklistReservaService
      .decisoes(reservaId, item.chave)
      .then((lista) => {
        if (!vivo) return
        setDecisoes(lista)
        setEstado('ok')
      })
      .catch((e) => {
        if (!vivo) return
        setErro(mensagemErroChecklistReserva(e, 'Não foi possível carregar o histórico.'))
        setEstado(ehForaDoEscopo(e) ? 'semPermissao' : 'erro')
      })
    return () => {
      vivo = false
    }
  }, [open, reservaId, item, tentativa])

  if (!item) return null

  return (
    <Dialog open={open} onClose={onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Histórico: {rotuloDoItem(item)}</DialogTitle>
      <DialogContent>
        {estado === 'carregando' ? (
          <EstadoDeLista estado="carregando" />
        ) : estado === 'semPermissao' ? (
          <EstadoDeLista estado="semPermissao" mensagem={erro ?? undefined} />
        ) : estado === 'erro' ? (
          <EstadoDeLista
            estado="erro"
            mensagem={erro ?? undefined}
            acao={{ label: 'Tentar novamente', onClick: () => setTentativa((t) => t + 1) }}
          />
        ) : decisoes.length === 0 ? (
          <EstadoDeLista estado="vazio" mensagem="Nenhuma decisão registrada para este item." />
        ) : (
          <Stack spacing={1.5} component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Decisões">
            {decisoes.map((d) => (
              <Box component="li" key={d.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={d.decisao === 'VALIDAR' ? 'success' : 'error'}
                    label={ROTULOS_DECISAO_ITEM[d.decisao] ?? d.decisao}
                  />
                  {d.origem === 'REUSO' && <Chip size="small" variant="outlined" label="Validação herdada" />}
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(d.ocorridoEm)}
                  </Typography>
                </Stack>
                {d.codigoMotivo && (
                  <Typography variant="body2">Código: {ROTULOS_CODIGO_MOTIVO[d.codigoMotivo] ?? d.codigoMotivo}</Typography>
                )}
                {d.motivo && <Typography variant="body2">Motivo: {d.motivo}</Typography>}
                {d.dataEmissao && (
                  <Typography variant="body2">Emitido em {formatDate(d.dataEmissao)}</Typography>
                )}
                {d.validoAte && <Typography variant="body2">Válido até {formatDate(d.validoAte)}</Typography>}
                {d.statusAnterior && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Status anterior: {ROTULOS_STATUS_ITEM[d.statusAnterior] ?? d.statusAnterior}
                  </Typography>
                )}
                {mostrarAutor && d.autorNome && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Por {d.autorNome}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}

export default HistoricoDecisoesDialog
